/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

export const AuthenticationStepMessages = defineMessages({
  title: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.title',
    defaultMessage: 'Authentication'
  },
  subtitle: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.subtitle',
    defaultMessage:
      'Authentication types are determined in the OpenAPI document and provide security for the extension.'
  },
  authenticationType: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.authenticationType',
    defaultMessage: 'Authentication type'
  },
  grantType: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.grantType',
    defaultMessage: 'Grant type'
  },
  serverUrlRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.serverUrlRequired',
    defaultMessage: 'Server URL is required'
  },
  serverVarPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.serverVarPlaceholder',
    defaultMessage: 'Enter a value'
  },
  serverUrlInvalid: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.serverUrlInvalid',
    defaultMessage: 'Server URL is not valid'
  },
  nonEmptyValue: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.nonEmptyValue',
    defaultMessage: 'Server URL variables cannot be empty'
  },
  accessTokenUrl: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.accessTokenUrl',
    defaultMessage: 'Access Token URL from openAPI spec'
  },
  accessTokenURLRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.accessTokenURLRequired',
    defaultMessage: 'Access token url is required'
  },
  username: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.username',
    defaultMessage: 'Username'
  },
  usernamePlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.usernamePlaceholder',
    defaultMessage: 'Enter a username'
  },
  usernameRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.usernameRequired',
    defaultMessage: 'Username is required'
  },
  usernameNonEmpty: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.usernameNonEmpty',
    defaultMessage: 'Username cannot be empty'
  },
  clientId: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientId',
    defaultMessage: 'Client ID'
  },
  clientIdPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientIdPlaceholder',
    defaultMessage: 'Enter a Client ID'
  },
  clientIdTooltip: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientIdTooltip',
    defaultMessage:
      'The client identifier issued to the client during the application registration process.'
  },
  clientIdRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientIdRequired',
    defaultMessage: 'Client ID is required'
  },
  clientIdNonEmpty: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientIdNonEmpty',
    defaultMessage: 'Client ID cannot be empty'
  },
  clientSecret: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientSecret',
    defaultMessage: 'Client secret'
  },
  clientSecretPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientSecretPlaceholder',
    defaultMessage: 'Enter a Client Secret'
  },
  clientSecretTooltip: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientSecretTooltip',
    defaultMessage:
      'The client secret issued to the client during the application registration process.'
  },
  clientSecretRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientSecretRequired',
    defaultMessage: 'Client Secret is required'
  },
  clientSecretNonEmpty: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientSecretNonEmpty',
    defaultMessage: 'Client Secret cannot be empty'
  },
  password: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.password',
    defaultMessage: 'Password'
  },
  passwordPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.passwordPlaceholder',
    defaultMessage: 'Enter a password'
  },
  passwordRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.passwordRequired',
    defaultMessage: 'Password is required'
  },
  passwordMaxChars: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.passwordMaxChars',
    defaultMessage: 'Password cannot exceed 512 characters.'
  },
  passwordNonEmpty: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.passwordNonEmpty',
    defaultMessage: 'Password cannot be empty'
  },
  token: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.token',
    defaultMessage: 'Token'
  },
  tokenPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.tokenPlaceholder',
    defaultMessage: 'Enter a token'
  },
  tokenRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.tokenRequired',
    defaultMessage: 'Token is required'
  },
  nonEmptyToken: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.nonEmptyToken',
    defaultMessage: 'Token cannot be empty'
  },
  apiKey: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.apiKey',
    defaultMessage: 'API key'
  },
  apiKeyPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.apiKeyPlaceholder',
    defaultMessage: 'Enter an API key'
  },
  apiKeyRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.apiKeyRequired',
    defaultMessage: 'API key is required'
  },
  nonEmptyApiKey: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.nonEmptyApiKey',
    defaultMessage: 'API key cannot be empty'
  },
  url: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.url',
    defaultMessage: 'URL'
  },
  dropdownLabel: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.dropdownLabel',
    defaultMessage: 'Choose server url'
  },
  serverVariableRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.serverVariableRequired',
    defaultMessage: '{name} is required and must not contain spaces'
  },
  variableRequired: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.variableRequired',
    defaultMessage: '{name} is required'
  },
  scope: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.scope',
    defaultMessage: 'Scope'
  },
  clientAuthType: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientAuthType',
    defaultMessage: 'Client authentication'
  },
  clientAuthTypeTooltip: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.clientAuthTypeTooltip',
    defaultMessage:
      'Whether the authentication credentials should be sent in the request body or in an HTTP header.'
  },
  body: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.body',
    defaultMessage: 'Send as Body'
  },
  custom: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.custom',
    defaultMessage: 'Custom'
  },
  basicAuthHeader: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.basicAuthHeader',
    defaultMessage: 'Send as Basic Auth header'
  },
  contentType: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.contentType',
    defaultMessage: 'Content type'
  },
  contentTypePlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.contentTypePlaceholder',
    defaultMessage: 'Enter a Content Type'
  },
  headerPrefix: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.headerPrefix',
    defaultMessage: 'Header prefix'
  },
  headerPrefixPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.headerPrefixPlaceholder',
    defaultMessage: 'Enter a Header prefix'
  },
  headerPrefixTooltip: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.headerPrefixTooltip',
    defaultMessage:
      'The prefix to add to the Authorization header, before the access token.'
  },
  customSecretsHeader: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.customParamHeader',
    defaultMessage: 'Custom Secrets'
  },
  customSecret: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.customSecret',
    defaultMessage: '{secretName}'
  },
  customParamHeader: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.customParamHeader',
    defaultMessage: 'Custom Param'
  },
  customParam: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.customParam',
    defaultMessage: '{customParam}'
  },
  customSecretsPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.customSecretsPlaceholder',
    defaultMessage: 'Enter a secret key'
  },
  customParamPlaceholder: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.customParamPlaceholder',
    defaultMessage: 'Enter a param'
  },
  noFlowsFoundTitle: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.noFlowsFoundTitle',
    defaultMessage: 'No flows found'
  },
  noFlowsFoundDescription: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.noFlowsFoundDescription',
    defaultMessage:
      'Your uploaded OpenAPI file needs supported flow types (password or client credentials) for OAuth configuration!'
  },
  authCodeFound: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.authCodeFound',
    defaultMessage:
      'Access granted! You can reuse the button "Regrant Access" below to regrant access'
  },
  grantAccess: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.grantAccess',
    defaultMessage: 'Grant Access'
  },
  reGrantAccess: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.reGrantAccess',
    defaultMessage: 'Regrant Access'
  },
  copyToClipBoard: {
    id: 'CatalogOpenApi.AuthenticationStepMessages.copyToClipBoard',
    defaultMessage: `Copy the below Redirect url to your OAuth app's 'Callback URL' field.`
  },
  serverVariablesNoEmpty: {
    id: 'CatalogOpenApi.serverVariablesNoEmpty',
    defaultMessage: 'Server variable cannot contain only spaces'
  },
  authRemovedTitle: {
    id: 'CatalogOpenApi.authRemovedTitle',
    defaultMessage: 'Authentication re-configuration required'
  },
  authRemovedSubtitle: {
    id: 'CatalogOpenApi.authRemovedSubtitle',
    defaultMessage: `The authentication configured for this extension has been removed with a recent replacement of the extension's OpenAPI document and is no longer available. You must re-configure your Live extension authentication below.`
  },
  serverUrlRemovedTitle: {
    id: 'CatalogOpenApi.serverUrlRemovedTitle',
    defaultMessage: 'Server URL re-configuration required'
  },
  serverUrlRemovedSubtitle: {
    id: 'CatalogOpenApi.serverUrlRemovedSubtitle',
    defaultMessage: `The server URL configured for this extension has been removed with a recent replacement of the extension's OpenAPI document and is no longer available. You must re-configure your Live extension server URL below.`
  },
  reEnterSecret: {
    id: 'CatalogOpenApi.reEnterSecret',
    defaultMessage: 'Please re-enter the secret value for security purposes'
  },
  newGrantReenter: {
    id: 'CatalogOpenApi.newGrantReenter',
    defaultMessage: 'New grant, please enter or re-enter the client secret'
  }
});
